/**
 * Checkout Page
 * Klorum
 *
 * Multi-gateway checkout:
 * - MercadoPago (Primary - Peru)
 * - PayPal (Secondary - International)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Star as StarIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

// MercadoPago Logo (inline SVG)
const MercadoPagoLogo = () => (
  <svg width="80" height="30" viewBox="0 0 100 40">
    <rect width="100" height="40" rx="8" fill="#00AEEF" />
    <text x="50" y="27" textAnchor="middle" fill="white" fontWeight="bold" fontSize="18">
      MP
    </text>
  </svg>
);

// PayPal Logo (inline SVG)
const PayPalLogo = () => (
  <svg width="80" height="30" viewBox="0 0 100 40">
    <rect width="100" height="40" rx="8" fill="#003087" />
    <text x="50" y="27" textAnchor="middle" fill="white" fontWeight="bold" fontSize="14">
      PayPal
    </text>
  </svg>
);

const Checkout = () => {
  const { user } = React.useContext(AuthContext);
  // States
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('business');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [gateway, setGateway] = useState('mercadopago');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Customer info
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerName, setCustomerName] = useState('');

  // Active step
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    i18n.t("checkout.gateways.steps.selectPlan"),
    i18n.t("checkout.gateways.steps.paymentMethod"),
    i18n.t("checkout.gateways.steps.confirm"),
  ];

  useEffect(() => {
    fetchPlans();
  }, [gateway]);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const currency = gateway === 'mercadopago' ? 'PEN' : 'USD';
      const response = await api.get(`/checkout/plans?currency=${currency}`);
      setPlans(response.data.plans || []);
    } catch (err) {
      setError(i18n.t("checkout.gateways.errors.loadPlans"));
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!customerEmail || !customerName) {
      setError(i18n.t("checkout.gateways.errors.missingCustomerInfo"));
      return;
    }

    setProcessing(true);
    setError(null);

    const companyId = user?.companyId || user?.company?.id;
    if (!companyId) {
      setError(i18n.t("checkout.gateways.errors.processPayment"));
      setProcessing(false);
      return;
    }

    try {
      const response = await api.post('/checkout/create', {
        plan: selectedPlan,
        billing_cycle: billingCycle,
        gateway: gateway,
        customer_email: customerEmail,
        customer_name: customerName,
        company_id: companyId,
      });

      if (gateway === 'mercadopago') {
        const redirectUrl = response.data.init_point || response.data.sandbox_init_point;
        if (redirectUrl) {
          window.location.href = redirectUrl;
        } else {
          setError(i18n.t("checkout.gateways.errors.initMercadoPago"));
        }
      } else if (gateway === 'paypal') {
        // Redirect to PayPal
        window.location.href = response.data.approval_url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || i18n.t("checkout.gateways.errors.processPayment"));
    } finally {
      setProcessing(false);
    }
  };

  const getSelectedPlanData = () => {
    return plans.find((p) => p.id === selectedPlan) || {};
  };

  const getPrice = () => {
    const plan = getSelectedPlanData();
    return billingCycle === 'monthly'
      ? plan.price_monthly
      : plan.price_yearly;
  };

  const getCurrency = () => (gateway === 'mercadopago' ? 'S/' : '$');

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        {i18n.t("checkout.gateways.title")}
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        {i18n.t("checkout.gateways.subtitle")}
      </Typography>

      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Plans Section */}
        <Grid item xs={12} md={8}>
          {/* Billing Cycle Toggle */}
          <Box display="flex" justifyContent="center" mb={3}>
            <Box
              sx={{
                bgcolor: 'grey.100',
                borderRadius: 3,
                p: 0.5,
                display: 'inline-flex',
              }}
            >
              <Button
                variant={billingCycle === 'monthly' ? 'contained' : 'text'}
                onClick={() => setBillingCycle('monthly')}
                sx={{ borderRadius: 2 }}
              >
                {i18n.t("checkout.gateways.billing.monthly")}
              </Button>
              <Button
                variant={billingCycle === 'yearly' ? 'contained' : 'text'}
                onClick={() => setBillingCycle('yearly')}
                sx={{ borderRadius: 2 }}
              >
                {i18n.t("checkout.gateways.billing.yearly")}
                <Chip
                  label={i18n.t("checkout.gateways.billing.freeMonths", { count: 2 })}
                  size="small"
                  color="success"
                  sx={{ ml: 1 }}
                />
              </Button>
            </Box>
          </Box>

          {/* Plans Grid */}
          <Grid container spacing={2}>
            {plans
              .filter((p) => Number(p.price_monthly || 0) > 0)
              .map((plan) => (
                <Grid item xs={12} sm={6} md={3} key={plan.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: selectedPlan === plan.id ? 2 : 1,
                      borderColor:
                        selectedPlan === plan.id ? 'primary.main' : 'grey.300',
                      position: 'relative',
                      '&:hover': { borderColor: 'primary.main' },
                    }}
                    onClick={() => setSelectedPlan(plan.id)}
                  >
                    {plan.id === 'business' && (
                      <Chip
                        icon={<StarIcon />}
                        label={i18n.t("checkout.gateways.plan.mostPopular")}
                        color="primary"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -12,
                          left: '50%',
                          transform: 'translateX(-50%)',
                        }}
                      />
                    )}
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold">
                        {plan.name}
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" mt={2}>
                        {getCurrency()}
                        {billingCycle === 'monthly'
                          ? plan.price_monthly
                          : plan.price_yearly}
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                        >
                          {billingCycle === "monthly"
                            ? i18n.t("checkout.gateways.plan.perMonth")
                            : i18n.t("checkout.gateways.plan.perYear")}
                        </Typography>
                      </Typography>

                      {billingCycle === 'yearly' && plan.savings_yearly > 0 && (
                        <Chip
                          label={i18n.t("checkout.gateways.plan.savings", {
                            amount: `${getCurrency()}${plan.savings_yearly}`,
                          })}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ mt: 1 }}
                        />
                      )}

                      <Divider sx={{ my: 2 }} />

                      <Box>
                        <Typography variant="body2" mb={1}>
                          {i18n.t("checkout.gateways.plan.limits.messagesPerMonth", {
                            value: plan.limits.messages.toLocaleString(),
                          })}
                        </Typography>
                        <Typography variant="body2" mb={1}>
                          {i18n.t("checkout.gateways.plan.limits.channels", {
                            value: plan.limits.channels,
                          })}
                        </Typography>
                        <Typography variant="body2">
                          {i18n.t("checkout.gateways.plan.limits.users", {
                            value: plan.limits.users,
                          })}
                        </Typography>
                      </Box>

                      <Radio
                        checked={selectedPlan === plan.id}
                        sx={{ mt: 2 }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>

          {/* Customer Info */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {i18n.t("checkout.gateways.billingInfo.title")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={i18n.t("checkout.gateways.billingInfo.fullName")}
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={i18n.t("checkout.gateways.billingInfo.email")}
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Summary & Payment */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: 'sticky', top: 20 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {i18n.t("checkout.gateways.summary.title")}
              </Typography>

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>
                  {i18n.t("checkout.gateways.summary.plan", { plan: getSelectedPlanData().name })}
                </Typography>
                <Typography fontWeight="bold">
                  {getCurrency()}
                  {getPrice()}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="body2" color="text.secondary">
                  {i18n.t("checkout.gateways.summary.cycle", {
                    cycle:
                      billingCycle === "monthly"
                        ? i18n.t("checkout.gateways.billing.monthly")
                        : i18n.t("checkout.gateways.billing.yearly"),
                  })}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box display="flex" justifyContent="space-between" mb={3}>
                <Typography variant="h6">{i18n.t("common.total")}</Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {getCurrency()}
                  {getPrice()}
                </Typography>
              </Box>

              {/* Payment Method */}
              <Typography variant="subtitle2" gutterBottom>
                {i18n.t("checkout.gateways.paymentMethod.title")}
              </Typography>

              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={gateway}
                  onChange={(e) => setGateway(e.target.value)}
                >
                  <FormControlLabel
                    value="mercadopago"
                    control={<Radio />}
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        <MercadoPagoLogo />
                        <Typography variant="body2">
                          {i18n.t("checkout.gateways.paymentMethod.mercadoPago")}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      border: 1,
                      borderColor: gateway === 'mercadopago' ? 'primary.main' : 'grey.300',
                      borderRadius: 1,
                      p: 1,
                      mb: 1,
                    }}
                  />
                  <FormControlLabel
                    value="paypal"
                    control={<Radio />}
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        <PayPalLogo />
                        <Typography variant="body2">
                          {i18n.t("checkout.gateways.paymentMethod.paypal")}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      border: 1,
                      borderColor: gateway === 'paypal' ? 'primary.main' : 'grey.300',
                      borderRadius: 1,
                      p: 1,
                    }}
                  />
                </RadioGroup>
              </FormControl>

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleCheckout}
                disabled={processing || !customerEmail || !customerName}
                sx={{ mt: 3 }}
              >
                {processing ? (
                  <CircularProgress size={24} />
                ) : gateway === 'mercadopago' ? (
                  i18n.t("checkout.gateways.buttons.payWithMercadoPago")
                ) : (
                  i18n.t("checkout.gateways.buttons.payWithPayPal")
                )}
              </Button>

              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                textAlign="center"
                mt={2}
              >
                {i18n.t("checkout.gateways.securityHint")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

    </Box>
  );
};

export default Checkout;
